import { Component } from '@theme/component';

/**
 * The customer media strip: arrows that scroll it, and a corner player.
 *
 * Two decisions worth knowing before changing anything here.
 *
 * The tiles are poster images. No <video> exists in the strip at all until
 * somebody opens one, which is the whole point: the theme this replaces puts a
 * real <source> plus `autoplay loop` on every tile, so a five-item strip
 * started five downloads and five decoders inside the product column.
 *
 * The player is a NON-modal dialog. `show()`, not `showModal()`, because the
 * design keeps the page usable behind it -- it is a corner player, not a
 * lightbox, and there is no backdrop. That costs the two things a modal gives
 * for free, so both are wired by hand below: Escape closes it, and focus goes
 * in on open and comes back to the tile on close.
 *
 * @extends {Component}
 */
class MmSocialProofComponent extends Component {
  requiredRefs = ['strip', 'dialog', 'dialogImage', 'dialogVideo'];

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  connectedCallback() {
    super.connectedCallback();

    // The player is moved to the end of <body> and kept there. A z-index of
    // two billion was not enough on its own: measured, it still painted under
    // the product cards of the band below and under the images further down,
    // because it competes inside whatever stacking context its ancestors
    // build, not against the page. As a child of body it has none above it.
    //
    // The reference is cached because refs only track descendants of the
    // component, and this node stops being one.
    this.#dialog = this.refs.dialog;
    document.body.appendChild(this.#dialog);

    this.#onClose = () => {
      this.#releaseVideo();
      this.#opener?.focus();
      this.#opener = null;
    };
    // A non-modal dialog gets no Escape handling from the browser, and the key
    // has to be caught on the document because focus may well be back on the
    // page by then -- that is the point of the thing not being modal.
    this.#onKeydown = (event) => {
      if (event.key === 'Escape' && this.#dialog?.open) this.#dialog.close();
    };
    this.#onScroll = () => this.#syncArrows();

    // The close button is wired by hand, not with on:click. Horizon's
    // delegation resolves the method on the nearest Component ancestor, and
    // once the dialog moves to body it has none -- measured: the click landed
    // nowhere and the player stayed open.
    this.#onCloseClick = () => this.#dialog?.close();
    this.#dialog.querySelector('[ref="close"]')?.addEventListener('click', this.#onCloseClick);

    this.#dialog.addEventListener('close', this.#onClose);
    document.addEventListener('keydown', this.#onKeydown);
    this.refs.strip.addEventListener('scroll', this.#onScroll, { passive: true });

    // No #syncArrows() call here: the observer reports once as soon as it
    // starts observing, after the browser has laid the page out. Calling it
    // by hand as well measured the strip in the middle of the page load, which
    // forced a whole extra layout (0.16 to 0.18s on a throttled phone).
    this.#observer = new ResizeObserver(() => this.#syncArrows());
    this.#observer.observe(this.refs.strip);
  }

  disconnectedCallback() {
    this.#dialog?.querySelector('[ref="close"]')?.removeEventListener('click', this.#onCloseClick);
    this.#dialog?.removeEventListener('close', this.#onClose);
    document.removeEventListener('keydown', this.#onKeydown);
    this.refs.strip?.removeEventListener('scroll', this.#onScroll);
    this.#observer?.disconnect();
    // It lives on body now, so nothing else would take it away.
    this.#dialog?.remove();
    super.disconnectedCallback?.();
  }

  updatedCallback() {
    if (this.#dialog?.open) this.#dialog.close();
    this.#syncArrows();
  }

  next() {
    this.refs.strip.scrollBy({ left: this.#step(), behavior: 'smooth' });
  }

  previous() {
    this.refs.strip.scrollBy({ left: -this.#step(), behavior: 'smooth' });
  }

  /** @param {Event} event */
  open(event) {
    const trigger = event.target;
    if (!(trigger instanceof Element)) return;

    const dialog = this.#dialog;
    const dialogImage = dialog.querySelector('[ref="dialogImage"]');
    const dialogVideo = dialog.querySelector('[ref="dialogVideo"]');
    const source = trigger.getAttribute('data-mm-video');
    this.#opener = trigger;

    if (source) {
      dialogImage.hidden = true;
      dialogVideo.poster = trigger.getAttribute('data-mm-image') ?? '';
      // Assigned last: setting src is what starts the download, so nothing is
      // fetched until a visitor asks for this particular video.
      dialogVideo.src = source;
      dialogVideo.hidden = false;
    } else {
      dialogVideo.hidden = true;
      dialogImage.src = trigger.getAttribute('data-mm-image') ?? '';
      dialogImage.alt = trigger.getAttribute('data-mm-alt') ?? '';
      const width = trigger.getAttribute('data-mm-width');
      const height = trigger.getAttribute('data-mm-height');
      if (width) dialogImage.setAttribute('width', width);
      if (height) dialogImage.setAttribute('height', height);
      dialogImage.hidden = false;
    }

    dialog.show();
    dialog.querySelector('[ref="close"]')?.focus();
    if (source) dialogVideo.play().catch(() => {});
  }

  close() {
    this.#dialog?.close();
  }

  /** One viewport of the strip, minus a tile, so the last one stays in sight. */
  #step() {
    const { strip } = this.refs;
    const tile = strip.querySelector('.mm-social-proof__item');
    const overlap = tile ? tile.getBoundingClientRect().width : 0;
    return Math.max(strip.clientWidth - overlap, strip.clientWidth * 0.5);
  }

  /**
   * The whole nav disappears when everything already fits, and each arrow
   * disables itself at its end of the track.
   */
  #syncArrows() {
    const { strip, nav, previous, next } = this.refs;
    if (!nav) return;

    const overflows = strip.scrollWidth - strip.clientWidth > 1;
    nav.hidden = !overflows;
    if (!overflows) return;

    const max = strip.scrollWidth - strip.clientWidth;
    if (previous) previous.disabled = strip.scrollLeft <= 1;
    if (next) next.disabled = strip.scrollLeft >= max - 1;
  }

  #releaseVideo() {
    const dialogVideo = this.#dialog?.querySelector('[ref="dialogVideo"]');
    if (!dialogVideo?.hasAttribute('src')) return;
    dialogVideo.pause();
    dialogVideo.removeAttribute('src');
    // Without load() the browser keeps the buffer and the decoder alive for a
    // video nobody is watching.
    dialogVideo.load();
  }

  /** @type {Element | null} */
  #opener = null;
  /** @type {ResizeObserver | undefined} */
  #observer;
  /** @type {() => void} */
  #onClose = () => {};
  /** @type {() => void} */
  #onCloseClick = () => {};
  /** @type {(event: KeyboardEvent) => void} */
  #onKeydown = () => {};
  /** @type {() => void} */
  #onScroll = () => {};
}

if (!customElements.get('mm-social-proof-component')) {
  customElements.define('mm-social-proof-component', MmSocialProofComponent);
}
